import * as nextI18next from 'next-i18next';
import {
  appWithTranslation,
  useTranslation,
  withTranslation,
} from 'next-i18next';

/**
 * i18n access for code that runs outside a React component.
 *
 * The important detail is *which* i18next instance this talks to. next-i18next
 * creates and owns one, and `serverSideTranslations` loads the locale resources
 * into it per request. Importing the `i18next` package directly hands you a
 * different, empty instance — `t()` on that one finds no resources and returns
 * the key it was given, which is why labels such as `paginator.current` rendered
 * verbatim.
 *
 * `nextI18next.i18n` is assigned when `appWithTranslation` initialises, so it is
 * read through the module namespace on every call rather than destructured once
 * at import — destructuring would capture the `null` it holds beforehand.
 *
 * Prefer `useTranslation()` inside components: it is bound to the instance for
 * the current render, whereas this reaches for a module-level global. That
 * distinction only matters if a second locale is ever added, since a global
 * instance has one language for the whole process.
 */
type TranslateOptions = Record<string, unknown>;

export function t(key: string, options?: TranslateOptions): string {
  const instance = nextI18next.i18n;

  if (!instance) {
    // Reached only if something translates before appWithTranslation has run —
    // typically a value computed at module scope. Returning the key keeps the
    // failure identical to i18next's own behaviour rather than throwing.
    if (process.env.NODE_ENV === 'development') {
      console.warn(
        `[i18n] t('${key}') ran before i18next was initialised, so the key ` +
          `will render instead of its translation. Move the call so it happens ` +
          `during render (see src/utils/i18n.ts).`
      );
    }

    return key;
  }

  return instance.t(key, options);
}

/**
 * Retained so the existing `I18n.t(...)` call sites keep working unchanged.
 */
export const i18n = { t };

export { appWithTranslation, useTranslation, withTranslation };
